import React, { useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import InboxOutlined from '@mui/icons-material/InboxOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import RequestQuoteOutlined from '@mui/icons-material/RequestQuoteOutlined';
import AddOutlined from '@mui/icons-material/AddOutlined';
import {
  CategoryPill,
  ConfirmDialog,
  ContextSelectPanel,
  ContextSelectRow,
  ContextSelectSection,
  EmptyState,
  FieldtimeDialog,
  FormField,
  PageToolbar,
  ReadOnlyField,
  SidebarContextBlock,
  StatusPill,
  WorkspaceSwitch,
  useNotify,
} from '.';
import {
  formatDate,
  formatDateTime,
  formatShortDate,
  formatWeekRange,
} from '../../utils/formatDate';
import { formatHrs, formatNumber, formatPercent } from '../../utils/formatNumber';

const AREAS = ['FS29C', 'MUS1A', 'MUS1B', 'L2 East', 'L2 West'];
const PMS = ['Bryan Bahr', 'Jeff Carter', 'Dana Lopez'];
const LONG_TEXT =
  'MUS1A EYD Prefab — Level 2 east wing branch circuits, panel feeders and the temporary power relocation for phase three';

const FORMATTER_ROWS = [
  ['formatDate', "'2026-02-10'", formatDate('2026-02-10')],
  ['formatDate', "'2026-02-10T00:00:00.000Z'", formatDate('2026-02-10T00:00:00.000Z')],
  ['formatDate', "'2026-02-31'", formatDate('2026-02-31')],
  ['formatDate', 'null', formatDate(null)],
  ['formatShortDate', "'2026-06-05'", formatShortDate('2026-06-05')],
  ['formatDateTime', "'2026-06-01T13:53:00'", formatDateTime('2026-06-01T13:53:00')],
  ['formatWeekRange', "'2026-10-11'", formatWeekRange('2026-10-11')],
  ['formatWeekRange', "'2026-11-01'", formatWeekRange('2026-11-01')],
  ['formatWeekRange', "'2027-01-03'", formatWeekRange('2027-01-03')],
  ['formatNumber', '32335.5', formatNumber(32335.5)],
  ['formatNumber', '-14685', formatNumber(-14685)],
  ['formatNumber', '843, { signed: true }', formatNumber(843, { signed: true })],
  ['formatHrs', '8', formatHrs(8)],
  ['formatHrs', '8.5', formatHrs(8.5)],
  ['formatHrs', '1784', formatHrs(1784)],
  ['formatHrs', '556, { withUnit: true }', formatHrs(556, { withUnit: true })],
  ['formatHrs', '0, { zeroAsDash: true }', formatHrs(0, { zeroAsDash: true })],
  ['formatHrs', '-480.8', formatHrs(-480.8)],
  ['formatPercent', '112', formatPercent(112)],
  ['formatPercent', '117.9, { decimals: 1 }', formatPercent(117.9, { decimals: 1 })],
  ['formatPercent', '661225', formatPercent(661225)],
  ['formatPercent', '50, { base: 0 }', formatPercent(50, { base: 0 })],
  ['formatPercent', "'abc'", formatPercent('abc')],
];

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const Section = ({ title, children }) => {
  const { ft } = useTheme().palette;
  return (
    <Box
      component="section"
      sx={{ py: 3, borderBottom: `1px solid ${ft.neutrals.divider}` }}
    >
      <Typography
        variant="sectionLabel"
        component="h2"
        sx={{ display: 'block', mb: 2, color: ft.neutrals.textSecondary }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
};

const Row = ({ children }) => (
  <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
    {children}
  </Box>
);

const PillsSection = () => {
  const notify = useNotify();
  return (
    <Section title="Pills">
      <Row>
        <StatusPill tone="success" label="Complete" />
        <StatusPill tone="warning" label="Waiting on materials" />
        <StatusPill tone="error" label="Blocked" />
        <StatusPill tone="info" label="Installation" />
        <StatusPill tone="neutral" label="Not started" />
        <StatusPill tone="neutralOutline" label="No bid" />
        <StatusPill tone="info" label="Prefab required" icon={<InfoOutlined />} />
        <StatusPill
          tone="warning"
          label="3 exceptions"
          icon={<WarningAmberOutlined />}
          title="Unresolved shop sync exceptions"
          onClick={() => notify.info('Pill clicked')}
        />
        <CategoryPill category="field" />
        <CategoryPill category="SHOP" />
        <StatusPill label={null} />
        <CategoryPill category="office" />
      </Row>
    </Section>
  );
};

const FieldsSection = () => {
  const [area, setArea] = useState('');
  const [trade, setTrade] = useState('');
  const [pm, setPm] = useState(null);
  const [start, setStart] = useState(null);
  const [hours, setHours] = useState('');

  return (
    <Section title="Form fields">
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 2,
          maxWidth: 760,
        }}
      >
        <FormField label="Area" required helperText="The area code from ACC">
          <TextField
            size="small"
            placeholder="FS29C"
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
        </FormField>
        <FormField label="Trade">
          <Select size="small" value={trade} onChange={(e) => setTrade(e.target.value)} displayEmpty>
            <MenuItem value="">
              <em>Choose a trade</em>
            </MenuItem>
            <MenuItem value="electrical">Electrical</MenuItem>
            <MenuItem value="mechanical">Mechanical</MenuItem>
            <MenuItem value="plumbing">Plumbing</MenuItem>
          </Select>
        </FormField>
        <FormField label="PM">
          <Autocomplete
            size="small"
            options={PMS}
            value={pm}
            onChange={(e, value) => setPm(value)}
            renderInput={(params) => <TextField {...params} placeholder="Bryan Bahr" />}
          />
        </FormField>
        <FormField label="Field installation start date" helperText="Sundays only">
          <DatePicker
            id="ui-kit-start-date"
            value={start}
            onChange={setStart}
            renderInput={(params) => (
              <TextField {...params} id="ui-kit-start-date" size="small" />
            )}
          />
        </FormField>
        <FormField label="Week ending" error="Pick a Sunday">
          <TextField size="small" value="10/08/2026" />
        </FormField>
        <FormField label="ST hours" touch helperText="Touch size (44px), for foreman screens">
          <TextField
            size="small"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' } }}
          />
        </FormField>
        <ReadOnlyField label="Trade (read only)" value="Electrical" />
        <FormField label="Trade (editable)">
          <TextField size="small" defaultValue="Electrical" />
        </FormField>
        <ReadOnlyField label="Description (long value)" value={LONG_TEXT} />
        <ReadOnlyField label="Notes (empty)" value="" />
        <Box sx={{ gridColumn: { md: '1 / -1' } }}>
          <ReadOnlyField label="Description (multiline)" value={LONG_TEXT} multiline />
        </Box>
      </Box>
    </Section>
  );
};

const EmptyStatesSection = () => (
  <Section title="Empty states">
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
        gap: 2,
        maxWidth: 760,
      }}
    >
      <EmptyState
        icon={<InboxOutlined />}
        title="No time entered for this day"
        description="Add an entry above to get started."
        action={<Button size="small">Add entry</Button>}
      />
      <EmptyState compact title="No records found." />
    </Box>
  </Section>
);

const DialogsSection = () => {
  const notify = useNotify();
  const [open, setOpen] = useState(null);
  const [overBoardValue, setOverBoardValue] = useState('');
  const close = () => setOpen(null);

  return (
    <Section title="Dialogs">
      <Row>
        <Button variant="outlined" onClick={() => setOpen('full')}>
          Open dialog
        </Button>
        <Button variant="outlined" onClick={() => setOpen('overBoard')}>
          Open over-board dialog
        </Button>
        <Button variant="outlined" color="error" onClick={() => setOpen('confirm')}>
          Open confirm dialog
        </Button>
      </Row>

      <FieldtimeDialog
        open={open === 'full'}
        onClose={close}
        title="Download package status for every package in the MUS1A EYD Prefab workflow this week"
        subtitle="MUS1A · Workflow: MUS1A EYD Prefab"
        meta="Updated Sep 18 by Bryan Bahr"
        status={<StatusPill tone="info" label="Prefab required" />}
        footerCaption="MUS1A_EYD_Prefab_Package_Status_Thru_2026-09-27_with_all_columns.xlsx"
        actions={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" onClick={close}>
              Download
            </Button>
          </>
        }
      >
        {Array.from({ length: 30 }, (_, i) => (
          <Typography key={i} sx={{ fontSize: 14, mb: 1 }}>
            Body line {i + 1}. The header and footer stay put while this scrolls.
          </Typography>
        ))}
      </FieldtimeDialog>

      <FieldtimeDialog
        open={open === 'overBoard'}
        onClose={close}
        overBoard
        title="Over-board dialog"
        subtitle="Renders above Bryntum popups (z-index 99,999)"
        actions={
          <>
            <Button onClick={close}>Cancel</Button>
            <Button variant="contained" onClick={close}>
              Save
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormField label="Area">
            <Select
              size="small"
              value={overBoardValue}
              onChange={(e) => setOverBoardValue(e.target.value)}
            >
              {AREAS.map((a) => (
                <MenuItem key={a} value={a}>
                  {a}
                </MenuItem>
              ))}
            </Select>
          </FormField>
          <Box>
            <Tooltip title="This tooltip also renders above the board">
              <Button variant="outlined">Hover for a tooltip</Button>
            </Tooltip>
          </Box>
        </Box>
      </FieldtimeDialog>

      <ConfirmDialog
        open={open === 'confirm'}
        onClose={close}
        title="Delete package E1421?"
        message="Its timecard records are kept, but the package is removed from the board."
        confirmLabel="Delete package"
        destructive
        onConfirm={async () => {
          await wait(1500);
          notify.success('Package deleted');
        }}
      />
    </Section>
  );
};

const NotificationsSection = () => {
  const notify = useNotify();
  return (
    <Section title="Notifications">
      <Row>
        <Button variant="outlined" onClick={() => notify.success('Package updated')}>
          Success
        </Button>
        <Button variant="outlined" onClick={() => notify.info('Snapshot regeneration started')}>
          Info
        </Button>
        <Button variant="outlined" onClick={() => notify.warning('3 packages were skipped')}>
          Warning
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            notify.error('Couldn’t reach the server. Check your connection and try again.')
          }
        >
          Error
        </Button>
        <Button
          variant="outlined"
          onClick={() =>
            notify({
              message: 'Package archived',
              undo: async () => {
                await wait(300);
                notify.info('Package restored');
              },
            })
          }
        >
          With undo
        </Button>
      </Row>
    </Section>
  );
};

const ToolbarSection = () => {
  const { ft } = useTheme().palette;
  const notify = useNotify();
  const [area, setArea] = useState(null);
  const [pm, setPm] = useState(null);

  return (
    <Section title="Page toolbar">
      <Box
        sx={{
          border: `1px dashed ${ft.neutrals.divider}`,
          borderRadius: `${ft.radius.control}px`,
        }}
      >
        <PageToolbar
          filters={
            <>
              <FormField label="Area" sx={{ width: 200 }}>
                <Autocomplete
                  size="small"
                  options={AREAS}
                  value={area}
                  onChange={(e, value) => setArea(value)}
                  renderInput={(params) => <TextField {...params} placeholder="All areas" />}
                />
              </FormField>
              <FormField label="PM" sx={{ width: 200 }}>
                <Autocomplete
                  size="small"
                  options={PMS}
                  value={pm}
                  onChange={(e, value) => setPm(value)}
                  renderInput={(params) => <TextField {...params} placeholder="All PMs" />}
                />
              </FormField>
            </>
          }
          status={
            <Typography variant="caption">
              Last ACC sync {formatDateTime('2026-10-02T12:16:00')}
            </Typography>
          }
          actions={
            <>
              <Button variant="outlined">Edit project areas</Button>
              <Button variant="contained">Add package</Button>
            </>
          }
          adminActions={[
            {
              label: 'Regenerate all snapshots',
              onClick: () => notify.info('Regenerate all snapshots clicked'),
            },
            { label: 'Recalculate headcount', onClick: () => {}, disabled: true },
          ]}
        />
      </Box>
    </Section>
  );
};

const FormattersSection = () => {
  const { ft } = useTheme().palette;
  const headSx = {
    ...ft.type.tableHeader,
    color: ft.neutrals.textSecondary,
    backgroundColor: ft.neutrals.headerRow,
  };
  const cellSx = { ...ft.type.tableCell, color: ft.neutrals.textPrimary };

  return (
    <Section title="Formatters">
      <Table size="small" sx={{ maxWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>Function</TableCell>
            <TableCell sx={headSx}>Input</TableCell>
            <TableCell sx={headSx}>Output</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {FORMATTER_ROWS.map(([fn, input, output]) => (
            <TableRow key={`${fn}-${input}`}>
              <TableCell sx={cellSx}>{fn}</TableCell>
              <TableCell sx={{ ...cellSx, fontFamily: 'monospace' }}>{input}</TableCell>
              <TableCell sx={cellSx}>{output}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Section>
  );
};

const WORKSPACE_OPTIONS = [
  { value: 'project', label: 'Projects', icon: <ApartmentOutlined /> },
  { value: 'bid', label: 'Bids', icon: <RequestQuoteOutlined /> },
];

const SAMPLE_BIDS = [
  { id: 1, name: "St. Luke's Hospital Central Plant Renovation", number: '210-26751', group: 'open', tone: 'info', status: 'Estimating' },
  { id: 2, name: 'Riverside Data Center Phase 2', number: '210-26749', group: 'open', tone: 'info', status: 'Estimating' },
  { id: 3, name: 'Olathe ISD Middle School HVAC Replacement', number: '290-26412', group: 'open', tone: 'warning', status: 'Submitted' },
  { id: 4, name: 'NRD2A-Mech North Kansas City', number: '210-26690', group: 'closed', tone: 'success', status: 'Won' },
  { id: 5, name: 'Wichita Water Treatment Plant Upgrade', number: '225-26655', group: 'closed', tone: 'neutralOutline', status: 'No bid' },
];

// Dark strip standing in for the sidebar, at its expanded or collapsed width.
const SidebarSample = ({ collapsed = false, children }) => {
  const { ft } = useTheme().palette;
  return (
    <Box
      sx={{
        width: collapsed ? 96 : 256,
        py: 2,
        backgroundColor: ft.sidebar.bg,
        borderRadius: `${ft.radius.card}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      {children}
    </Box>
  );
};

const SidebarSection = () => {
  const { ft } = useTheme().palette;
  const [workspace, setWorkspace] = useState('bid');
  const [panelOpen, setPanelOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(2);
  const [search, setSearch] = useState('');
  const query = search.trim().toLowerCase();
  const matches = SAMPLE_BIDS.filter(
    (bid) => !query || `${bid.name} ${bid.number}`.toLowerCase().includes(query)
  );
  const selected = SAMPLE_BIDS.find((bid) => bid.id === selectedId);

  const renderRows = (group) =>
    matches
      .filter((bid) => bid.group === group)
      .map((bid) => (
        <ContextSelectRow
          key={bid.id}
          primary={bid.name}
          secondary={bid.number}
          end={<StatusPill tone={bid.tone} label={bid.status} />}
          selected={bid.id === selectedId}
          onClick={() => {
            setSelectedId(bid.id);
            setPanelOpen(false);
          }}
        />
      ));

  return (
    <Section title="Sidebar workspace">
      <Row>
        <SidebarSample>
          <SidebarContextBlock
            headline={selected.name}
            subline={selected.number}
            tooltip={`${selected.name} — ${selected.number}`}
            open={panelOpen}
            onClick={() => setPanelOpen(true)}
          />
          <SidebarContextBlock
            headline="NRD2A-Mech North Kansas City Data Center Expansion Phase Three"
            subline="210-26690"
            tooltip="NRD2A-Mech North Kansas City Data Center Expansion Phase Three — 210-26690"
          />
          <SidebarContextBlock emptyLabel="Select a bid" />
          <Box sx={{ px: 2 }}>
            <WorkspaceSwitch
              options={WORKSPACE_OPTIONS}
              value={workspace}
              onChange={setWorkspace}
            />
          </Box>
        </SidebarSample>
        <SidebarSample collapsed>
          <SidebarContextBlock
            collapsed
            headline={selected.name}
            chipLabel={selected.number}
            tooltip={`${selected.name} — ${selected.number}`}
            onClick={() => setPanelOpen(true)}
          />
          <SidebarContextBlock collapsed emptyLabel="Select a bid" />
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <WorkspaceSwitch
              collapsed
              options={WORKSPACE_OPTIONS}
              value={workspace}
              onChange={setWorkspace}
            />
          </Box>
        </SidebarSample>
      </Row>
      <ContextSelectPanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title="Select bid"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by name or bid number"
        searchAriaLabel="Search bids"
        footer={
          <>
            <Button variant="text" sx={{ color: ft.neutrals.textBody }}>
              View all bids
            </Button>
            <Button variant="outlined" startIcon={<AddOutlined />}>
              New bid
            </Button>
          </>
        }
      >
        {matches.length === 0 ? (
          <EmptyState compact title="No bids match your search." />
        ) : (
          <>
            <ContextSelectSection>Open bids</ContextSelectSection>
            {renderRows('open')}
            <ContextSelectSection>Recently closed</ContextSelectSection>
            {renderRows('closed')}
          </>
        )}
      </ContextSelectPanel>
    </Section>
  );
};

/**
 * UiKitPage — /dev/ui-kit (super admin only, not in the sidebar). Shows every
 * shared component from Components/ui in every state, plus the formatters.
 */
const UiKitPage = () => {
  const { ft } = useTheme().palette;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          px: { xs: 2, md: 3 },
          pb: 6,
          backgroundColor: ft.neutrals.white,
          color: ft.neutrals.textBody,
          '& .MuiButton-root': { textTransform: 'none', fontWeight: 500 },
        }}
      >
        <Box sx={{ pt: 3 }}>
          <Typography variant="dialogTitle" component="h1" sx={{ color: ft.neutrals.textPrimary }}>
            UI kit
          </Typography>
          <Typography variant="subtitle" component="p" sx={{ color: ft.neutrals.textSecondary }}>
            Shared components from Components/ui (#289). See docs/UI_Design_Standards_v1.md.
          </Typography>
        </Box>
        <PillsSection />
        <FieldsSection />
        <EmptyStatesSection />
        <DialogsSection />
        <NotificationsSection />
        <ToolbarSection />
        <SidebarSection />
        <FormattersSection />
      </Box>
    </LocalizationProvider>
  );
};

export default UiKitPage;
